import React from "react";
import { Container, Header, EmptySpace, Section } from "./Timeline2025.styled";
import TabSlider from "../../../components/atoms/TabSlider";
import { MONTHS_LIST } from "./constants";
import CardList from "./components/CardList/CardList";
import { func } from "../../../utils";
import { useFetchCurrentEventSeries } from "../../../networks";
import { TabSliderOptionsProps } from "../../../components/atoms/TabSlider/TabSlider";
import useWindowSize from "../../../hooks/useWindowSize";
import { defaultCurrentMonthTimeline2025, generateCardType, getUpcomingMonth } from "./utils";
import { TimelineCardType } from "./components/CardItem/hooks/useTimelineCardType";
import { ChipTabs } from "./components";
import { ChipTabsOption } from "./components/ChipTabs/ChipTabs";
import { useCurrentTimelineSection } from "../../../store/user";

export interface DataEventSeriesItemsProps {
  id: number;
  region_name: string;
  slug: string;
  event_description: string;
  registration_start_date: string;
  registration_end_date: string;
  event_start_date: string;
  event_end_date: string;
  key_visual: string;
  key_visual_mobile: string;
  showing_order: string;
  created_at: string;
  updated_at: string;
  status: "upcoming" | "live" | "past";
  event_location: string;
  video_url: string;
  registration_announcement_date: string;
  is_registration_open: boolean;
  registration_url: string;
  card_type: TimelineCardType;
  dance_registration_start_date: string;
  dance_registration_end_date: string;
}

export interface DataEventSeriesProps {
  month: string;
  events: DataEventSeriesItemsProps[];
}

const Timeline2025 = () => {
  const { size } = useWindowSize();
  //   @ts-ignore
  const isMobileView = (size?.width || 0) <= 768;

  const currentMonth = defaultCurrentMonthTimeline2025();
  const upcomingMonthIdx = getUpcomingMonth(currentMonth?.month);

  const [activeMonth, setActiveMonth] = React.useState<string>("");
  const [defaultIndex, setDefaultIndex] = React.useState<number>(0);
  const [alreadyChangeTab, setAlreadyChangeTab] = React.useState<boolean>(false);

  const { setSection, section } = useCurrentTimelineSection();

  const { data: dataEventSeriesRaw, isFetching } = useFetchCurrentEventSeries();
  const dataEventSeries: DataEventSeriesProps[] = dataEventSeriesRaw?.data?.data?.eventSeries ?? [];

  const dataWithActiveCard =
    [...dataEventSeries]?.filter((data) => {
      /** Registration */
      if (section === "registration") {
        const isHaveActiveCard = data?.events?.some((val) => val?.status === "live");
        return isHaveActiveCard;
      }

      const isHaveActiveCard = data?.events?.find((val) => {
        const cardType = generateCardType({
          eventStartDate: val?.event_start_date,
          eventEndDate: val?.event_end_date,
          registrationStartDate: val?.registration_start_date,
          registrationEndDate: val?.registration_end_date,
          videoUrl: val?.video_url,
          registrationUrl: val?.registration_url,
          isRegistrationOpen: val?.is_registration_open,
        });

        const activeWatchStatus = [
          "before_event_in_registration_period",
          "before_event_not_in_registration_period",
          "event_day_without_video_url",
          "event_day_with_video_url",
        ];

        return activeWatchStatus?.includes(cardType);
      });

      return isHaveActiveCard;
    })?.[0] || null;

  const defaultValIndex = !!dataWithActiveCard
    ? MONTHS_LIST?.findIndex((data) => data?.value === currentMonth?.month)
    : upcomingMonthIdx;

  const defaultTabIndexVal = defaultValIndex;

  const dataEventSeriesByMonth =
    [...dataEventSeries]?.find(
      (data) => String(data?.month)?.toLowerCase() === String(activeMonth)?.toLowerCase(),
    )?.events || [];

  const dataEventSeriesByMonthWithCardType = [...dataEventSeriesByMonth]?.map((data) => {
    const cardType = generateCardType({
      eventStartDate: data?.event_start_date,
      eventEndDate: data?.event_end_date,
      registrationStartDate: data?.registration_start_date,
      registrationEndDate: data?.registration_end_date,
      videoUrl: data?.video_url,
      registrationUrl: data?.registration_url,
      isRegistrationOpen: data?.is_registration_open,
    });

    return { ...data, card_type: cardType };
  });

  const orderEventSeries = {
    live: 0,
    upcoming: 1,
    past: 2,
  };

  const watchOrderEventSeries = {
    // Different priority for watch section
    event_day_with_video_url: 0, // Put videos first
    event_day_without_video_url: 1,
    before_event_in_registration_period: 2,
    before_event_not_in_registration_period: 3,
    upcoming: 4,
    past_event: 5,
  };

  const dataOrderedEventSeries = React.useMemo(() => {
    const dataToSort = [...dataEventSeriesByMonthWithCardType];

    if (section === "registration") {
      return dataToSort.sort((a, b) => {
        const aValue = orderEventSeries[a.status] !== undefined ? orderEventSeries[a.status] : 999;
        const bValue = orderEventSeries[b.status] !== undefined ? orderEventSeries[b.status] : 999;
        return aValue - bValue;
      });
    } else {
      return dataToSort.sort((a, b) => {
        if (a.card_type === "past_event") return 1;
        if (b.card_type === "past_event") return -1;

        const aValue =
          watchOrderEventSeries[a.card_type] !== undefined
            ? watchOrderEventSeries[a.card_type]
            : 999;
        const bValue =
          watchOrderEventSeries[b.card_type] !== undefined
            ? watchOrderEventSeries[b.card_type]
            : 999;
        return aValue - bValue;
      });
    }
  }, [dataEventSeriesByMonthWithCardType, section]);

  const handleActiveMonth = (item: TabSliderOptionsProps) => {
    if (!alreadyChangeTab) {
      setAlreadyChangeTab(true);
    }
    setActiveMonth(item?.value);
  };

  const handleChangeSection = (section) => {
    if (dataWithActiveCard) {
      const defaultIndex =
        MONTHS_LIST?.findIndex((data) => data?.value === dataWithActiveCard?.month) ||
        defaultTabIndexVal;
      setDefaultIndex(defaultIndex || MONTHS_LIST?.[0]?.index);
      setActiveMonth(dataWithActiveCard?.month || MONTHS_LIST?.[0]?.value);
    } else {
      console.log("HERE");
      setDefaultIndex(defaultTabIndexVal || MONTHS_LIST?.[0]?.index);
      setActiveMonth(currentMonth?.month || MONTHS_LIST?.[0]?.value);
    }
  };

  const handleActiveSection = (option: ChipTabsOption) => {
    setSection(option?.index === 0 ? "registration" : "watch");

    if (!alreadyChangeTab) {
      handleChangeSection(option?.index === 0 ? "registration" : "watch");
    }
  };

  React.useEffect(() => {
    if (dataWithActiveCard) {
      const defaultIndex =
        MONTHS_LIST?.findIndex((data) => data?.value === dataWithActiveCard?.month) ||
        defaultTabIndexVal;
      setDefaultIndex(defaultIndex || MONTHS_LIST?.[0]?.index);
      setActiveMonth(dataWithActiveCard?.month || MONTHS_LIST?.[0]?.value);
    } else {
      setDefaultIndex(defaultTabIndexVal || MONTHS_LIST?.[0]?.index);
      setActiveMonth(MONTHS_LIST?.[defaultTabIndexVal]?.value || MONTHS_LIST?.[0]?.value);
    }
  }, [defaultTabIndexVal, dataWithActiveCard]);

  return (
    <Container>
      <Header>
        <img
          src={func.getStaticImg(
            `${
              isMobileView
                ? "/typography/timeline-2025-title.png"
                : "/typography/timeline-2025-title-desktop.png"
            }`,
          )}
        />
      </Header>

      <EmptySpace height={"6px"} />
      <ChipTabs
        options={[
          {
            index: 0,
            title: isMobileView ? "DAFTARKAN SEKOLAHMU" : "DAFTARKAN SEKOLAH KAMU",
          },
          {
            index: 1,
            title: "TONTON PIALA BY.U",
          },
        ]}
        onChangeTab={handleActiveSection}
      />
      <EmptySpace height={"32px"} />

      <Section>
        <TabSlider
          defaultIndexCardLeftSpace={isMobileView ? 0 : 25}
          defaultIndex={defaultIndex}
          options={MONTHS_LIST}
          onClick={handleActiveMonth}
        />
      </Section>

      <EmptySpace height="32px" />
      <CardList data={dataOrderedEventSeries} isLoading={isFetching} />

      <EmptySpace height="42px" />
    </Container>
  );
};

export default Timeline2025;
